import clsx from 'clsx';
import React from 'react';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import List, { ListRowRenderer } from 'react-virtualized/dist/commonjs/List';
import WindowScroller from 'react-virtualized/dist/commonjs/WindowScroller';

import CustomResizeObserver from 'lib/ResizeObserver';

import { useStyles } from './styles';

const LARGE_DISPLAY_RATIO = 0.4 / 0.3;

type RenderElementProps = {
	key: string;
	styleProps: React.CSSProperties;
	rowFiles: number;
};

type BaseElement = {
	id: string;
	width: number;
	height: number;
};

export type RenderFlexElementFn<T> = (element: T, props: RenderElementProps) => React.ReactElement;
type FlexDisplayMode = 'normal' | 'large';

export type SameWidthProps = {
	minWidth: number;
	maxWidth: number;
};

export type ComponentProps<T extends BaseElement> = {
	elements: T[];
	displayMode: FlexDisplayMode;
	renderElement: RenderFlexElementFn<T>;
	maxItemsPerRow: number;
	titleHeight?: number;
	gutterValue?: number;
	containerClassName?: string;
	isMobile?: boolean;
	sameWidthProps?: SameWidthProps;
	scrollElement?: Element | typeof window;
};

// TODO: Use the new flex size calculator
const getElementRatio = (element: BaseElement) => element.width / element.height;

export const calculateElementMaxWidth = (element: BaseElement, containerWidth: number, displayMode: FlexDisplayMode) => {
	const ratio = getElementRatio(element);
	const factor = 0.3 * (displayMode === 'large' ? LARGE_DISPLAY_RATIO : 1);
	return ratio * factor * containerWidth;
};
export const calculateElementMinWidth = (element: BaseElement, containerWidth: number) => Math.min(getElementRatio(element) * 210, containerWidth);
export const calculateFlexGrow = (element: BaseElement) => getElementRatio(element) * 2;
export const calculateElementWidth = (element: BaseElement, containerWidth: number, displayMode: FlexDisplayMode, limitWidth: boolean) => {
	const ratio = getElementRatio(element);
	const maxWidth = calculateElementMaxWidth(element, containerWidth, displayMode);
	const elementWidth = ratio * 0.2 * (displayMode === 'large' ? LARGE_DISPLAY_RATIO : 1) * containerWidth;
	const width = limitWidth ? Math.min(elementWidth, maxWidth) : elementWidth;
	return Math.max(width, calculateElementMinWidth(element, containerWidth));
};
export const calculateHeight = (element: BaseElement, newWidth: number) => {
	const ratio = getElementRatio(element);
	return newWidth / ratio;
};

export const calculateFlexElementEqualWidth = (containerWidth: number, itemsPerRow: number) => {
	return Math.floor(containerWidth / itemsPerRow);
};

export const calculateElementFlexWidth = (
	element: BaseElement,
	containerWidth: number,
	remainingSpace: number,
	totalFlexGrow: number,
	displayMode: FlexDisplayMode,
	limitWidth: boolean
) => {
	const width = calculateElementWidth(element, containerWidth, displayMode, limitWidth);
	const maxWidth = calculateElementMaxWidth(element, containerWidth, displayMode);
	const flexGrow = calculateFlexGrow(element);
	const flexElementWidth = (flexGrow / totalFlexGrow) * remainingSpace + width;
	return limitWidth ? Math.min(flexElementWidth, maxWidth) : flexElementWidth;
};

export const buildAllRows = <T extends BaseElement>(
	elements: T[],
	maxItemsPerRow: number,
	gutterValue: number,
	containerWidth: number,
	displayMode: FlexDisplayMode,
	limitWidth: boolean,
	sameWidthProps?: SameWidthProps,
	itemsPerRow?: number
): T[][] => {
	const result = [];
	const screenWidth = containerWidth; // Can be overriden if there is a scrollbar for example
	let row = [];
	let remainingWidth = screenWidth;

	for (const image of elements) {
		const newWidth = !sameWidthProps
			? calculateElementWidth(image, containerWidth, displayMode, limitWidth) + gutterValue
			: calculateFlexElementEqualWidth(containerWidth, itemsPerRow);
		if (remainingWidth - newWidth < 0 || row.length === maxItemsPerRow) {
			result.push(row);
			row = [image];
			remainingWidth = screenWidth - newWidth;
		} else {
			row.push(image);
			// Handle too big for width
			remainingWidth = remainingWidth - newWidth;
		}
	}
	result.push(row);
	return result;
};

const FlexLayout = <T extends BaseElement>(props: ComponentProps<T>) => {
	const {
		elements,
		displayMode,
		containerClassName = '',
		gutterValue = 15,
		isMobile = false,
		maxItemsPerRow,
		scrollElement = window,
		titleHeight = 0,
		sameWidthProps = null
	} = props;
	const { renderElement } = props;

	const classes = useStyles({ margin: gutterValue });
	const [containerWidth, setContainerWidth] = React.useState(1500);
	const containerRef = React.useRef<HTMLDivElement>();
	const listRef = React.useRef<List>();

	const recompute = React.useCallback(() => {
		setContainerWidth(containerRef.current?.clientWidth);
		listRef.current?.recomputeRowHeights();
	}, []);

	useUpdateEffect(() => {
		recompute();
	}, [elements, recompute]);

	React.useEffect(() => {
		setContainerWidth(containerRef.current.clientWidth);
	}, []);

	React.useEffect(() => {
		const { current: containerElement } = containerRef;
		const observer = new CustomResizeObserver(() => {
			recompute();
		});
		observer.observe(containerElement);
		return () => {
			observer.unobserve(containerElement);
		};
	}, [recompute]);

	const itemsPerRow = React.useMemo(() => {
		if (!sameWidthProps) return null;
		let itemsPerRow = Math.min(Math.ceil(containerWidth / sameWidthProps.maxWidth), maxItemsPerRow);
		return Math.min(itemsPerRow, Math.floor(containerWidth / sameWidthProps.minWidth));
	}, [containerWidth, maxItemsPerRow, sameWidthProps]);

	const generateStyleRules = React.useCallback(
		(element: BaseElement): React.CSSProperties => {
			const ratio = getElementRatio(element);
			const flexGrow = calculateFlexGrow(element);
			const maxWidth = !sameWidthProps
				? calculateElementMaxWidth(element, containerWidth, displayMode)
				: Math.min(containerWidth / itemsPerRow, sameWidthProps.maxWidth);
			return {
				flex: displayMode === 'large' ? `${flexGrow} 1 ${ratio * 25}%` : `${flexGrow} 1 ${ratio * 20}%`,
				minWidth: !sameWidthProps
					? calculateElementMinWidth(element, containerWidth)
					: Math.max(containerWidth / itemsPerRow, sameWidthProps.minWidth),
				maxWidth: !isMobile ? maxWidth : '0 !important',
				padding: gutterValue / 2,
				height: '100%',
				boxSizing: 'border-box'
			};
		},
		[containerWidth, displayMode, gutterValue, isMobile, sameWidthProps, itemsPerRow]
	);

	const rows = React.useMemo(
		() => buildAllRows(elements, maxItemsPerRow, gutterValue, containerWidth, displayMode, !isMobile, sameWidthProps, itemsPerRow),
		[containerWidth, displayMode, elements, gutterValue, isMobile, maxItemsPerRow, sameWidthProps, itemsPerRow]
	);
	const computeRowHeight = React.useCallback(
		({ index }: { index: number }): number => {
			const row = rows[index];
			let elementsWidth = 0;
			let totalFlexGrow = 0;
			if (row.length === 0) return 0;
			row.forEach(element => {
				elementsWidth += calculateElementWidth(element, containerWidth, displayMode, !isMobile);
				totalFlexGrow += calculateFlexGrow(element);
			});
			const remainingSpace = containerWidth - elementsWidth;
			const resizedElements = row.map(element => {
				const newWidth = !sameWidthProps
					? calculateElementFlexWidth(element, containerWidth, remainingSpace, totalFlexGrow, displayMode, !isMobile)
					: calculateFlexElementEqualWidth(containerWidth, itemsPerRow);
				const newHeight = calculateHeight(element, newWidth);
				return { width: newWidth, height: newHeight };
			});
			const maxHeight = Math.max(...resizedElements.map(element => element.height));
			return titleHeight + maxHeight;
		},
		[rows, containerWidth, titleHeight, displayMode, isMobile, sameWidthProps, itemsPerRow]
	);

	const buildRow: ListRowRenderer = React.useCallback(
		({ index, key, style }) => {
			const row = rows[index];
			return (
				<div key={key} style={style} className={classes.resultsRow}>
					{row.map(element =>
						renderElement(element, { key: `flexItem-${element.id}`, styleProps: generateStyleRules(element), rowFiles: row.length })
					)}
				</div>
			);
		},
		[rows, classes, renderElement, generateStyleRules]
	);

	return (
		<div className={clsx(classes.resultsList, containerClassName)} ref={containerRef}>
			<WindowScroller scrollElement={scrollElement}>
				{({ height, isScrolling, onChildScroll, scrollTop, registerChild }) => (
					<AutoSizer disableHeight style={{ height: 'auto' }}>
						{({ width }) => (
							<div ref={registerChild}>
								<List
									ref={listRef}
									autoHeight
									width={width}
									height={height}
									rowCount={rows.length}
									rowHeight={computeRowHeight}
									rowRenderer={buildRow}
									isScrolling={isScrolling}
									onScroll={onChildScroll}
									scrollTop={scrollTop}
								></List>
							</div>
						)}
					</AutoSizer>
				)}
			</WindowScroller>
		</div>
	);
};

export default React.memo(FlexLayout) as typeof FlexLayout;
